import { useState } from "react";
import { useCustom, useCustomMutation, useApiUrl, useTranslate } from "@refinedev/core";
import {
  Button,
  ColorField,
  Field,
  Modal,
  SectionHeader,
  SelectField,
  type SelectOption,
} from "@fawkes/ui";

import { getAccessToken } from "../auth";

interface Tag {
  id: number;
  label: string;
  color: string;
}

// The six tag categories of the unified hub. ANIMAL is the standalone /api/animal-tags resource
// (jve-6-2, no usage tracking); the other five are the jve-6-5-1 /api/tag-types/{type} surface with
// usage + reassign. `path` is the dataProvider-relative base; `hasUsage` drives the delete dialog.
interface Category {
  key: string;
  labelKey: string;
  path: string;
  hasUsage: boolean;
}
const CATEGORIES: Category[] = [
  { key: "klant-kleur-code", labelKey: "tagsHub.category.customer", path: "tag-types/klant-kleur-code", hasUsage: true },
  { key: "animal", labelKey: "tagsHub.category.animal", path: "animal-tags", hasUsage: false },
  { key: "animal-note", labelKey: "tagsHub.category.note", path: "tag-types/animal-note", hasUsage: true },
  { key: "animal-file", labelKey: "tagsHub.category.file", path: "tag-types/animal-file", hasUsage: true },
  { key: "klant-file", labelKey: "tagsHub.category.relatieFile", path: "tag-types/klant-file", hasUsage: true },
  {
    key: "pension-reservering",
    labelKey: "tagsHub.category.pensionReservering",
    path: "tag-types/pension-reservering",
    hasUsage: true,
  },
];

const DEFAULT_COLOR = "#5a7d6c"; // parity with the Jmix hub default

interface EditState {
  category: Category;
  id: number | null;
  label: string;
  color: string;
  options: Tag[]; // sibling tags in the category — reassign targets when deleting
}
interface DeleteState {
  category: Category;
  tag: Tag;
  usage: number;
  reassignTo: string;
  options: Tag[];
}

// jve-6-5 (full Jmix-UX revert): the Tags hub renders one card per category with the tags shown as
// colour chips; add/edit happens in a modal, and delete routes through a modal that surfaces usage +
// reassign for the tracked categories. Each card owns its fetch and refetches on a version bump.
export function TagsHub() {
  const t = useTranslate();
  const apiUrl = useApiUrl();
  const { mutate: mutateCustom, isLoading: isSaving } = useCustomMutation();

  const [version, setVersion] = useState(0);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [del, setDel] = useState<DeleteState | null>(null);
  const refresh = () => setVersion((v) => v + 1);

  function startCreate(category: Category) {
    setEdit({ category, id: null, label: "", color: DEFAULT_COLOR, options: [] });
  }
  function startEdit(category: Category, tag: Tag, options: Tag[]) {
    setEdit({ category, id: tag.id, label: tag.label, color: tag.color ?? DEFAULT_COLOR, options });
  }

  function save() {
    if (!edit) return;
    const values = { label: edit.label.trim(), color: edit.color };
    // Relative path: the dataProvider's `custom` prepends the /api base (see dataProvider.ts).
    // Prefixing with apiUrl here would double it to /api/api/... → 404.
    const base = edit.category.path;
    mutateCustom(
      { url: edit.id == null ? base : `${base}/${edit.id}`, method: edit.id == null ? "post" : "put", values },
      { onSuccess: () => { setEdit(null); refresh(); } },
    );
  }

  // From the edit modal: resolve usage, then either delete straight away or open the usage dialog.
  async function askDelete(category: Category, tag: Tag, options: Tag[]) {
    let usage = 0;
    if (category.hasUsage) {
      try {
        // Raw fetch (not via the dataProvider), so it DOES need the apiUrl base prepended.
        const res = await fetch(`${apiUrl}/${category.path}/${tag.id}/usage`, {
          headers: { Accept: "application/json", Authorization: `Bearer ${getAccessToken() ?? ""}` },
        });
        if (res.ok) usage = (await res.json()).count ?? 0;
      } catch {
        usage = 0;
      }
    }
    setEdit(null);
    if (usage === 0) {
      doDelete(category, tag.id, null);
    } else {
      setDel({ category, tag, usage, reassignTo: "", options: options.filter((o) => o.id !== tag.id) });
    }
  }

  function doDelete(category: Category, id: number, reassignTo: string | null) {
    const qs = reassignTo ? `?reassignTo=${reassignTo}` : "";
    mutateCustom(
      { url: `${category.path}/${id}${qs}`, method: "delete", values: {} },
      { onSuccess: () => { setDel(null); refresh(); } },
    );
  }

  const reassignOptions: SelectOption[] = del
    ? [
        { value: "", label: t("tagsHub.delete.choosePlaceholder") },
        ...del.options.map((o) => ({ value: String(o.id), label: o.label })),
      ]
    : [];

  return (
    <>
      <SectionHeader>{t("tagsHub.title")}</SectionHeader>
      <div className="fawkes-tags-grid">
        {CATEGORIES.map((category) => (
          <CategoryCard
            key={category.key}
            category={category}
            version={version}
            label={t(category.labelKey)}
            addLabel={t("animalTagListView.create")}
            emptyLabel={t("tagsHub.empty")}
            onAdd={() => startCreate(category)}
            onEdit={(tag, rows) => startEdit(category, tag, rows)}
          />
        ))}
      </div>

      {/* Add / edit modal */}
      <Modal
        open={edit != null}
        title={edit?.id == null ? t("tagsHub.create.title") : t("tagsHub.edit.title")}
        onClose={() => setEdit(null)}
        footer={
          edit && (
            <>
              {edit.id != null && (
                <Button
                  variant="secondary"
                  onClick={() => askDelete(edit.category, { id: edit.id!, label: edit.label, color: edit.color }, edit.options)}
                  disabled={isSaving}
                >
                  {t("animalTagListView.delete")}
                </Button>
              )}
              <span style={{ flex: 1 }} />
              <Button variant="secondary" onClick={() => setEdit(null)} disabled={isSaving}>{t("common.cancel")}</Button>
              <Button variant="primary" onClick={save} disabled={isSaving || (edit.label.trim() === "")}>{t("common.save")}</Button>
            </>
          )
        }
      >
        {edit && (
          <>
            <Field
              label={t("animalTagDetailView.label")}
              value={edit.label}
              onChange={(e) => setEdit({ ...edit, label: e.target.value })}
              required
            />
            <ColorField
              label={t("animalTagDetailView.color")}
              value={edit.color}
              onChange={(e) => setEdit({ ...edit, color: e.target.value })}
            />
          </>
        )}
      </Modal>

      {/* Delete-with-usage modal */}
      <Modal
        open={del != null}
        title={t("animalTagListView.delete")}
        onClose={() => setDel(null)}
        footer={
          del && (
            <>
              <Button variant="secondary" onClick={() => setDel(null)} disabled={isSaving}>{t("common.cancel")}</Button>
              <Button variant="secondary" onClick={() => doDelete(del.category, del.tag.id, null)} disabled={isSaving}>
                {t("tagsHub.delete.removeAndDelete")}
              </Button>
              <Button
                variant="primary"
                disabled={isSaving || del.reassignTo === ""}
                onClick={() => doDelete(del.category, del.tag.id, del.reassignTo)}
              >
                {t("tagsHub.delete.transferAndDelete")}
              </Button>
            </>
          )
        }
      >
        {del && (
          <div data-testid="delete-usage-dialog">
            <p className="fawkes-error">{t("tagsHub.delete.inUse", { args: [del.usage, del.tag.label] })}</p>
            <SelectField
              label={t("tagsHub.delete.transferTo")}
              options={reassignOptions}
              value={del.reassignTo}
              onChange={(e) => setDel({ ...del, reassignTo: e.target.value })}
            />
          </div>
        )}
      </Modal>
    </>
  );
}

interface CategoryCardProps {
  category: Category;
  version: number;
  label: string;
  addLabel: string;
  emptyLabel: string;
  onAdd: () => void;
  onEdit: (tag: Tag, rows: Tag[]) => void;
}

function CategoryCard({ category, version, label, addLabel, emptyLabel, onAdd, onEdit }: CategoryCardProps) {
  const t = useTranslate();
  const { data, isLoading, isError } = useCustom<Tag[]>({
    url: category.path,
    method: "get",
    queryOptions: { queryKey: ["tags", category.path, version] },
  });
  const rows = data?.data ?? [];

  return (
    <section className="fawkes-tag-card">
      <header className="fawkes-tag-card__head">
        <h3 className="fawkes-tag-card__title">{label}</h3>
        <Button variant="secondary" onClick={onAdd}>{addLabel}</Button>
      </header>
      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{t("tagsHub.loadFailed")}</p>
      ) : rows.length === 0 ? (
        <p className="fawkes-muted fawkes-tag-card__empty">{emptyLabel}</p>
      ) : (
        <div className="fawkes-tag-chips">
          {rows.map((tag) => (
            <button
              key={tag.id}
              type="button"
              className="fawkes-tag-chip"
              style={{ backgroundColor: tag.color }}
              onClick={() => onEdit(tag, rows)}
              data-testid="tag-pill"
              title={t("animalTagListView.edit")}
            >
              {tag.label}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
